import { WarningCircle } from "@phosphor-icons/react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { Apartment } from "@/api/districts/types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TableCell, TableRow } from "@/components/ui/table";

const COL_SPAN = 7;

export function DividerRow() {
	return (
		<tr aria-hidden="true" className="pointer-events-none">
			<td colSpan={COL_SPAN} className="px-3 py-0">
				<div className="h-px bg-border/40" />
			</td>
		</tr>
	);
}

export function SkeletonRows() {
	return (
		<>
			<DividerRow />
			{Array.from({ length: 5 }).map((_, i) => (
				<React.Fragment key={i}>
					<TableRow>
						{Array.from({ length: 7 }).map((_, j) => (
							<TableCell key={`skel-${j}`}>
								<Skeleton className="h-4 w-full" />
							</TableCell>
						))}
					</TableRow>
					{i < 4 && <DividerRow />}
				</React.Fragment>
			))}
		</>
	);
}

interface ApartmentRowProps {
	apartment: Apartment;
	onSelect: (id: number) => void;
	hideElectricity: boolean;
}

export function ApartmentRow({
	apartment,
	onSelect,
	hideElectricity,
}: ApartmentRowProps) {
	const { t } = useTranslation();
	const meters = apartment.current_meters;

	return (
		<TableRow>
			<TableCell className="font-medium">{apartment.street}</TableCell>
			<TableCell>{apartment.apartment_number}</TableCell>
			<TableCell className="text-muted-foreground">
				{meters?.cold_water_meter ?? "—"}
			</TableCell>
			<TableCell className="text-muted-foreground">
				{meters?.hot_water_meter ?? "—"}
			</TableCell>
			<TableCell className="text-muted-foreground">
				{meters?.heat_meter ?? "—"}
			</TableCell>
			{!hideElectricity && (
				<TableCell className="text-muted-foreground">
					{meters?.electricity_meter ?? "—"}
				</TableCell>
			)}
			<TableCell>
				<div className="flex items-center justify-end gap-1">
					{apartment.alerts.length > 0 && (
						<WarningCircle
							className="size-4 text-destructive"
							weight="fill"
							aria-label={`${apartment.alerts.length} alert(s)`}
						/>
					)}
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onSelect(apartment.id)}
					>
						{t("districts.details")}
					</Button>
				</div>
			</TableCell>
		</TableRow>
	);
}

interface ApartmentRowsProps {
	apartments: Apartment[];
	onSelect: (id: number) => void;
	search: string;
	hideElectricity: boolean;
}

export function ApartmentRows({
	apartments,
	onSelect,
	search,
	hideElectricity,
}: ApartmentRowsProps) {
	const { t } = useTranslation();

	if (apartments.length === 0) {
		return (
			<TableRow>
				<TableCell
					colSpan={hideElectricity ? 6 : 7}
					className="py-8 text-center text-muted-foreground"
				>
					{search ? t("apartments.noResults") : t("apartments.noApartments")}
				</TableCell>
			</TableRow>
		);
	}

	return (
		<>
			<DividerRow />
			{apartments.map((apt, i) => (
				<React.Fragment key={apt.id}>
					<ApartmentRow
						apartment={apt}
						onSelect={onSelect}
						hideElectricity={hideElectricity}
					/>
					{apartments[i + 1] !== undefined && <DividerRow />}
				</React.Fragment>
			))}
		</>
	);
}
